/**
 * AdminPanel.jsx
 *
 * SuperAdmin-only page. Accessible via /admin.
 * Route is gated in App.jsx — only users with isSuperAdmin: true can reach it.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import { usePinLock } from '../contexts/PinLockContext.jsx';
import { getPinStatus, adminResetPin, getTagsAdmin, deleteTag, createTag, updateTag, saveTagRule, backfillTagRules, previewTagRuleMatches, getAccessUsers, resolveInheritedAccess, addAccessUser, changeAccessRole, removeAccessUser, getAccessLogs } from '../api/client.js';
import DeveloperDocs from '../components/DeveloperDocs.jsx';
import EditClinicStaffModal from '../components/EditClinicStaffModal.jsx';
import StaffEmailInput from '../components/StaffEmailInput.jsx';
import DirectoryMatches from '../components/DirectoryMatches.jsx';
import PlainDropdown from '../components/PlainDropdown.jsx';
import { tagColourClass, tagPillStyle, isHexColour, TAG_COLOURS } from '../utils/tagColors.js';

// Shared tag colour picker: None + the palette swatches + a native custom-hex
// input. `value` is '' | 'tag-<name>' | '#rrggbb'; onChange emits the same.
function TagColourPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Colour</span>
      <button type="button" className="btn ghost sm" onClick={() => onChange('')}
              style={{ fontSize: 11, fontWeight: value === '' ? 700 : 400 }}>None</button>
      {TAG_COLOURS.map(c => {
        const cls = `tag-${c.name}`;
        return (
          <button key={c.name} type="button" title={c.name} aria-label={`Colour ${c.name}`}
                  onClick={() => onChange(cls)}
                  style={{
                    width: 18, height: 18, borderRadius: '50%', background: c.swatch,
                    border: value === cls ? '2px solid var(--text-1, #000)' : '1px solid var(--border)',
                    cursor: 'pointer', padding: 0,
                  }} />
        );
      })}
      <label title="Custom colour" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="color" aria-label="Custom colour" value={isHexColour(value) ? value : '#888888'}
               onChange={e => onChange(e.target.value)}
               style={{ width: 22, height: 22, padding: 0, borderRadius: 4, cursor: 'pointer',
                        border: isHexColour(value) ? '2px solid var(--text-1, #000)' : '1px solid var(--border)', background: 'none' }} />
        <span style={{ fontSize: 10, color: 'var(--text-3)' }}>Custom</span>
      </label>
    </div>
  );
}
import RuleBuilder from '../components/RuleBuilder.jsx';
import { emptyRule, ruleIsPreviewable } from '../utils/ruleFields.js';
import { CostCategoryPill } from '../components/CostChip.jsx';
import { CHANGELOG, IMPACT, sortedChangelog } from '../lib/changelog.js';
import NotificationsAdmin from '../components/NotificationsAdmin.jsx';
import RolesAdmin from '../components/RolesAdmin.jsx';
import ClinicAccessGrants from '../components/ClinicAccessGrants.jsx';
import { buildRuleFieldValues } from '../utils/ruleFieldValues.js';
import ConfigRegistry from '../components/ConfigRegistry.jsx';
import IncidentCategoriesEditor from '../components/IncidentCategoriesEditor.jsx';
import { ListSearch, Pager, usePagination, AdminTabBar, tabSlug, AdminFilterChip } from '../components/adminPrimitives.jsx';
import {
  validateConfigValue as validateSharedConfig, SLA_WEEKDAYS, SLA_OPS_GROUPS, SLA_HOURS_PER_LEG_MIN, SLA_HOURS_PER_LEG_MAX, SLA_DEFAULTS,
} from '@shared/configSchema.js';
import SecurityAlertMutes from '../components/SecurityAlertMutes.jsx';
import { sortRows, buildAllUserRows, matchesUserQuery } from '../utils/userSort.js';
import { FlagIcon } from '@phosphor-icons/react/dist/csr/Flag';
import { TrashIcon } from '@phosphor-icons/react/dist/csr/Trash';

// Badge primitives (Badge, SwatchPicker) + event-type controls now live in
// components/configControls.jsx — shared with the schema-driven ConfigRegistry.

// Cost-category pill styles — the .cost-* classes in styles/global.css.
const COST_STYLES = [
  'cost-refund', 'cost-corrective', 'cost-medical', 'cost-legal',
  'cost-compensation', 'cost-goodwill', 'cost-other',
];

function CostSwatchPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {COST_STYLES.map(cls => (
        <button
          key={cls}
          type="button"
          onClick={() => onChange(cls)}
          aria-pressed={value === cls}
          className={`cost-pill ${cls}`}
          style={{
            cursor: 'pointer',
            outline: value === cls ? '2px solid var(--brand)' : 'none',
            outlineOffset: 1,
          }}
        >
          {cls.replace('cost-', '')}
        </button>
      ))}
    </div>
  );
}

// ── Shared components ──────────────────────────────────────────────────────────

function SaveBanner({ error, onDismiss }) {
  if (!error) return null;
  return (
    <div className="callout danger" style={{ marginBottom: 16 }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <div style={{ flex: 1 }}><strong>Save failed</strong> — {error}</div>
      <button className="btn ghost sm" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

// Event-type controls (EventTypeAvailability / EventTypePills) and the badge
// Badge/SwatchPicker primitives are imported from components/configControls.jsx
// so the legacy sections below share them with the schema-driven ConfigRegistry.

// ── Section: Event Types ───────────────────────────────────────────────────────
function EventTypesSection({ eventTypes, onSave }) {
  const [items, setItems]     = useState(() => (eventTypes || []).map(e => ({ ...e })));
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft]     = useState(null);

  function startEdit(item) { setEditKey(item.key); setDraft({ ...item }); }
  function cancelEdit()    { setEditKey(null); setDraft(null); }
  function applyEdit() {
    setItems(prev => prev.map(i => i.key === draft.key ? { ...draft } : i));
    setDirty(true); cancelEdit();
  }
  function toggleEnabled(key) {
    setItems(prev => prev.map(i => i.key === key ? { ...i, enabled: !i.enabled } : i));
    setDirty(true);
  }
  async function save() {
    setSaving(true);
    try { await onSave('eventTypes', items); setDirty(false); }
    finally { setSaving(false); }
  }

  const sorted = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div>
      <div className="admin-section-head">
        <div>
          <div className="admin-section-title">Event Types</div>
          <div className="admin-section-desc">
            The event kinds shown on a new submission. The internal value is fixed (each type has its own form) — edit the label, hint, visibility and order.
          </div>
        </div>
        {dirty && (
          <button className="btn primary" onClick={save} disabled={saving} style={{ fontSize: 12 }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>

      <div className="admin-stack-sm">
        {sorted.map(item => (
          <div key={item.key}>
            {editKey === item.key && draft ? (
              <div className="admin-edit-card">
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                    <label style={{ fontSize: 11 }}>Label</label>
                    <input className="input" style={{ fontSize: 13 }} value={draft.label}
                      onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} />
                  </div>
                  <div className="field" style={{ width: 110, marginBottom: 0 }}>
                    <label style={{ fontSize: 11 }}>Order</label>
                    <input className="input" type="number" style={{ fontSize: 13 }} value={draft.order}
                      onChange={e => setDraft(d => ({ ...d, order: Number(e.target.value) || 0 }))} />
                  </div>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: 11 }}>Hint (shown under the card)</label>
                  <input className="input" style={{ fontSize: 13 }} value={draft.hint || ''}
                    onChange={e => setDraft(d => ({ ...d, hint: e.target.value }))} />
                </div>
                <div className="field-hint" style={{ fontSize: 11 }}>Value: <code>{draft.value}</code> (fixed)</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn primary sm" onClick={applyEdit} style={{ fontSize: 12 }}>Apply</button>
                  <button className="btn sm" onClick={cancelEdit} style={{ fontSize: 12 }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className={`admin-list-item${item.enabled ? '' : ' is-disabled'}`}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</span>
                <code style={{ fontSize: 11, color: 'var(--text-3)' }}>{item.value}</code>
                <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1 }}>{item.hint}</span>
                <button className="btn ghost sm" onClick={() => toggleEnabled(item.key)} style={{ fontSize: 11 }}>
                  {item.enabled ? 'Enabled' : 'Disabled'}
                </button>
                <button className="btn ghost sm" onClick={() => startEdit(item)} style={{ fontSize: 11 }}>Edit</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Section: Legal Threshold ───────────────────────────────────────────────────

function LegalThresholdSection({ months, onSave }) {
  const [value, setValue]   = useState(months);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const dirty = value !== months;

  async function save() {
    setSaving(true);
    try {
      await onSave('legalThresholdMonths', value);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="admin-section-title">Legal Threshold</div>
        <div className="admin-section-desc">
          Number of months from incident date until the legal deadline. Shown on the case timeline and PDF report.
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>Months</label>
          <input
            type="number" min="1" max="60" className="input"
            style={{ width: 80, fontSize: 13 }}
            value={value}
            onChange={e => { setValue(Number(e.target.value)); setSaved(false); }}
          />
        </div>
        <div style={{ marginTop: 20 }}>
          {dirty ? (
            <button className="btn primary" onClick={save} disabled={saving} style={{ fontSize: 12 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          ) : saved ? (
            <span style={{ fontSize: 12, color: 'var(--ok)', fontWeight: 500 }}>Saved</span>
          ) : null}
        </div>
        <div style={{ marginTop: 20, fontSize: 12, color: 'var(--text-3)' }}>
          Currently: <strong>{months} month{months !== 1 ? 's' : ''}</strong>
        </div>
      </div>
    </div>
  );
}

// ── Section: Response SLA (all coded treatment cases) ───────────────────────────────────────────
// Compact editor for the five governed SLA keys (sla_escalation_logic.md): the
// kill switch, hours per leg, the HQ and clinic-default working-hours calendars
// (one open/close pair per weekday + closed dates + HQ zones by ops) and the
// role-name → side map. Validated with the SHARED validators before an atomic
// batch publish (every key lands or none does).

const SLA_DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

// `withTz` — only the HQ calendar carries per-ops time zones; the clinic
// default calendar has none (SlaWeekEditor never shows them and
// slaCalendarFromDraft never emits them), so it is not seeded either.
function slaCalendarDraft(cal, seed, withTz = false) {
  const src = cal && typeof cal === 'object' ? cal : seed;
  const week = {};
  for (const d of SLA_WEEKDAYS) {
    const ivs = Array.isArray(src?.week?.[d]) ? src.week[d] : [];
    const first = Array.isArray(ivs[0]) ? ivs[0] : null;
    week[d] = { open: first ? first[0] : '09:00', close: first ? first[1] : '17:30', closed: !first, extra: ivs.slice(1) };
  }
  const draft = {
    week,
    closedDates: (Array.isArray(src?.closedDates) ? src.closedDates : []).join('\n'),
  };
  if (withTz) draft.tzByOps = src?.tzByOps ? { ...src.tzByOps } : null;
  return draft;
}

function slaCalendarFromDraft(draft, withTz) {
  const week = {};
  for (const d of SLA_WEEKDAYS) {
    const row = draft.week[d];
    week[d] = row.closed ? [] : [[row.open, row.close], ...(row.extra || [])];
  }
  const closedDates = String(draft.closedDates || '').split(/[\n,;]+/).map(x => x.trim()).filter(Boolean);
  const out = { week, closedDates };
  if (withTz) out.tzByOps = { ...(draft.tzByOps || SLA_DEFAULTS.slaHqCalendar.tzByOps) };
  return out;
}

function SlaWeekEditor({ title, draft, onChange, withTz }) {
  const set = (patch) => onChange({ ...draft, ...patch });
  const setDay = (d, patch) => set({ week: { ...draft.week, [d]: { ...draft.week[d], ...patch } } });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr 1fr', gap: '4px 10px', alignItems: 'center', fontSize: 12 }}>
        {SLA_WEEKDAYS.map(d => {
          const row = draft.week[d];
          return (
            <React.Fragment key={d}>
              <span style={{ fontWeight: 600, color: row.closed ? 'var(--text-3)' : 'var(--text)' }}>{SLA_DAY_LABELS[d]}</span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-3)' }}>
                <input type="checkbox" checked={row.closed} onChange={e => setDay(d, { closed: e.target.checked })} /> Closed
              </label>
              <input type="time" className="input" value={row.open} disabled={row.closed} onChange={e => setDay(d, { open: e.target.value })} style={{ fontSize: 12, padding: '4px 6px' }} aria-label={`${SLA_DAY_LABELS[d]} opens`} />
              <input type="time" className="input" value={row.close} disabled={row.closed} onChange={e => setDay(d, { close: e.target.value })} style={{ fontSize: 12, padding: '4px 6px' }} aria-label={`${SLA_DAY_LABELS[d]} closes`} />
            </React.Fragment>
          );
        })}
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label style={{ fontSize: 11 }}>Closed dates (YYYY-MM-DD, one per line)</label>
        <textarea className="input" rows={2} value={draft.closedDates} onChange={e => set({ closedDates: e.target.value })} style={{ fontSize: 12, resize: 'vertical' }} placeholder="2026-12-25" />
      </div>
      {withTz && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {SLA_OPS_GROUPS.map(ops => (
            <div className="field" key={ops} style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Time zone · {ops}</label>
              <input className="input" value={draft.tzByOps?.[ops] || ''} onChange={e => set({ tzByOps: { ...(draft.tzByOps || {}), [ops]: e.target.value } })} style={{ fontSize: 12 }} placeholder="Europe/Dublin" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SlaSection({ config, onSaveBatch }) {
  const seedFrom = (cfg) => ({
    enabled: cfg.slaEnabled === true,
    hours: Number.isInteger(cfg.slaHoursPerLeg) ? cfg.slaHoursPerLeg : SLA_DEFAULTS.slaHoursPerLeg,
    hoursLow: Number.isInteger(cfg.slaHoursPerLegLow) ? cfg.slaHoursPerLegLow : SLA_DEFAULTS.slaHoursPerLegLow,
    hq: slaCalendarDraft(cfg.slaHqCalendar, SLA_DEFAULTS.slaHqCalendar, true),
    clinic: slaCalendarDraft(cfg.slaClinicDefaultCalendar, SLA_DEFAULTS.slaClinicDefaultCalendar),
    hqRoles: (cfg.slaSides?.hq || SLA_DEFAULTS.slaSides.hq).join(', '),
    clinicRoles: (cfg.slaSides?.clinic || SLA_DEFAULTS.slaSides.clinic).join(', '),
  });
  const [draft, setDraft]   = useState(() => seedFrom(config));
  const [dirty, setDirty]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [errors, setErrors] = useState([]);

  const update = (patch) => { setDraft(d => ({ ...d, ...patch })); setDirty(true); setSaved(false); setErrors([]); };
  const splitRoles = (s) => String(s || '').split(/[,\n;]+/).map(x => x.trim()).filter(Boolean);

  function buildEntries() {
    return [
      { key: 'slaEnabled',               value: !!draft.enabled },
      { key: 'slaHoursPerLeg',           value: Number(draft.hours) },
      { key: 'slaHoursPerLegLow',        value: Number(draft.hoursLow) },
      { key: 'slaHqCalendar',            value: slaCalendarFromDraft(draft.hq, true) },
      { key: 'slaClinicDefaultCalendar', value: slaCalendarFromDraft(draft.clinic, false) },
      { key: 'slaSides',                 value: { hq: splitRoles(draft.hqRoles), clinic: splitRoles(draft.clinicRoles) } },
    ];
  }

  async function save() {
    const entries = buildEntries();
    const errs = entries.flatMap(e => validateSharedConfig(e.key, e.value).errors || []);
    if (errs.length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await onSaveBatch(entries, { publish: true });
      setDirty(false); setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setErrors([e.message || 'Publish failed']);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div className="admin-section-title">Response SLA</div>
        <div className="admin-section-desc">
          Whose turn it is on a coded treatment case (Code 1–5), with a working-hours timer that alternates between HQ (the Supervisors) and the clinic until the case is closed. Codes 4 and 5 run on the short turn, Codes 1–3 on the long one. Breaches escalate by email to the case's HoRO and Country Manager. Off until you switch it on.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={draft.enabled} onChange={e => update({ enabled: e.target.checked })} />
          Response SLA enabled
        </label>
        <div className="field" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>Working hours per turn — Codes 4 &amp; 5</label>
          <input type="number" className="input" min={SLA_HOURS_PER_LEG_MIN} max={SLA_HOURS_PER_LEG_MAX} value={draft.hours}
            onChange={e => update({ hours: Number(e.target.value) })} style={{ width: 80, fontSize: 13 }} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>Working hours per turn — Codes 1–3</label>
          <input type="number" className="input" min={SLA_HOURS_PER_LEG_MIN} max={SLA_HOURS_PER_LEG_MAX} value={draft.hoursLow}
            onChange={e => update({ hoursLow: Number(e.target.value) })} style={{ width: 80, fontSize: 13 }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 14 }}>
        <SlaWeekEditor title="HQ calendar (Supervisor turns)" draft={draft.hq} onChange={hq => update({ hq })} withTz />
        <SlaWeekEditor title="Clinic default calendar (when Maps has no schedule)" draft={draft.clinic} onChange={clinic => update({ clinic })} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 14 }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>HQ side — role names</label>
          <input className="input" value={draft.hqRoles} onChange={e => update({ hqRoles: e.target.value })} style={{ fontSize: 12 }} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>Clinic side — role names</label>
          <input className="input" value={draft.clinicRoles} onChange={e => update({ clinicRoles: e.target.value })} style={{ fontSize: 12 }} />
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12 }}>
        Role names as Entra / Admin ▸ Roles define them. Custom roles scoped to a country group (the Country Managers) count as HQ automatically.
      </div>

      {errors.length > 0 && (
        <div className="callout danger" style={{ marginBottom: 12, fontSize: 12 }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {dirty ? (
          <>
            <button className="btn primary" onClick={save} disabled={saving} style={{ fontSize: 12 }}>{saving ? 'Publishing…' : 'Publish'}</button>
            <button className="btn ghost sm" onClick={() => { setDraft(seedFrom(config)); setDirty(false); setErrors([]); }} disabled={saving} style={{ fontSize: 12 }}>Discard</button>
          </>
        ) : saved ? (
          <span style={{ fontSize: 12, color: 'var(--ok)', fontWeight: 500 }}>Published</span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            Currently: <strong>{config.slaEnabled === true ? 'on' : 'off'}</strong> · {config.slaHoursPerLeg ?? SLA_DEFAULTS.slaHoursPerLeg}h per turn on Codes 4–5 · {config.slaHoursPerLegLow ?? SLA_DEFAULTS.slaHoursPerLegLow}h on Codes 1–3
          </span>
        )}
      </div>
    </div>
  );
}

// ── Section: Page Size ─────────────────────────────────────────────────────────

function PageSizeSection({ pageSize, onSave }) {
  const OPTIONS = [10, 20, 50, 100];
  const [value, setValue]   = useState(pageSize);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const dirty = value !== pageSize;

  async function save() {
    setSaving(true);
    try { await onSave('defaultPageSize', value); setSaved(true); setTimeout(() => setSaved(false), 3000); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div className="admin-section-title">Default Page Size</div>
        <div className="admin-section-desc">Number of cases shown per page in the All Cases list. Takes effect on next page load.</div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {OPTIONS.map(n => (
          <button key={n} type="button"
            className={`btn${value === n ? ' primary' : ''}`}
            onClick={() => { setValue(n); setSaved(false); }}
            style={{ fontSize: 12, minWidth: 44 }}
          >
            {n}
          </button>
        ))}
        {dirty ? (
          <button className="btn primary" onClick={save} disabled={saving} style={{ fontSize: 12, marginLeft: 8 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        ) : saved ? (
          <span style={{ fontSize: 12, color: 'var(--ok)', fontWeight: 500, marginLeft: 8 }}>Saved</span>
        ) : null}
      </div>
    </div>
  );
}

// ── Section: Default Sort ──────────────────────────────────────────────────────

const SORT_COLUMNS = [
  { value: 'reportedDate', label: 'Reported date' },
  { value: 'clinic',       label: 'Clinic' },
  { value: 'severity',     label: 'Severity' },
  { value: 'status',       label: 'Status' },
  { value: 'treatment',    label: 'Treatment' },
  { value: 'staffMember',  label: 'Staff member' },
];

function DefaultSortSection({ defaultSort, onSave }) {
  const [col, setCol]       = useState(defaultSort.col);
  const [dir, setDir]       = useState(defaultSort.dir);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const dirty = col !== defaultSort.col || dir !== defaultSort.dir;

  async function save() {
    setSaving(true);
    try { await onSave('defaultSort', { col, dir }); setSaved(true); setTimeout(() => setSaved(false), 3000); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div className="admin-section-title">Default Sort</div>
        <div className="admin-section-desc">Initial sort applied when the All Cases list first loads. Takes effect on next page load.</div>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <select className="select" style={{ fontSize: 13, width: 180 }}
          value={col} onChange={e => { setCol(e.target.value); setSaved(false); }}>
          {SORT_COLUMNS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 4 }}>
          {['asc', 'desc'].map(d => (
            <button key={d} type="button"
              className={`btn${dir === d ? ' primary' : ''}`}
              onClick={() => { setDir(d); setSaved(false); }}
              style={{ fontSize: 12 }}
            >
              {d === 'asc' ? '↑ Oldest first' : '↓ Newest first'}
            </button>
          ))}
        </div>
        {dirty ? (
          <button className="btn primary" onClick={save} disabled={saving} style={{ fontSize: 12 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        ) : saved ? (
          <span style={{ fontSize: 12, color: 'var(--ok)', fontWeight: 500 }}>Saved</span>
        ) : null}
      </div>
    </div>
  );
}

// ── Section: Filter Group Visibility ──────────────────────────────────────────

const FILTER_GROUPS = [
  { key: 'status',    label: 'Status' },
  { key: 'treatment', label: 'Treatment' },
  { key: 'severity',  label: 'Severity' },
  { key: 'country',   label: 'Country' },
  { key: 'clinic',    label: 'Clinic' },
];

function FilterGroupsSection({ visible, onSave }) {
  const [state, setState]   = useState({ ...visible });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const dirty = FILTER_GROUPS.some(g => state[g.key] !== visible[g.key]);

  async function save() {
    setSaving(true);
    try { await onSave('visibleFilterGroups', state); setSaved(true); setTimeout(() => setSaved(false), 3000); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div className="admin-section-title">Filter Panel Groups</div>
          <div className="admin-section-desc">Toggle which filter groups are visible in the All Cases sidebar.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {dirty && (
            <button className="btn primary" onClick={save} disabled={saving} style={{ fontSize: 12 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          {!dirty && saved && <span style={{ fontSize: 12, color: 'var(--ok)', fontWeight: 500 }}>Saved</span>}
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {FILTER_GROUPS.map(g => (
          <label key={g.key} style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
            border: `1px solid ${state[g.key] ? 'var(--brand)' : 'var(--border)'}`,
            background: state[g.key] ? 'var(--brand-weak)' : 'var(--surface)',
            fontSize: 13, fontWeight: state[g.key] ? 600 : 400,
            color: state[g.key] ? 'var(--brand)' : 'var(--text-3)',
            transition: 'all .12s var(--ease)',
          }}>
            <input type="checkbox" style={{ display: 'none' }}
              checked={state[g.key]}
              onChange={e => { setState(s => ({ ...s, [g.key]: e.target.checked })); setSaved(false); }} />
            {g.label}
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Filter chip + compact admin data table — shared by Access log and Users panels.
 */
const ADMIN_TH = {
  padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-3)',
};
// height acts as a min-height on table cells: every row is at least 42px (the
// tallest natural row — one with a .btn.sm), so tables that share a card row
// (Users & access · Access log) line up row-for-row.
const ADMIN_TD = { padding: '7px 12px', verticalAlign: 'middle', height: 42 };

/** A clickable column header. Click to sort, click again to reverse. */
function SortTh({ label, sortKey, sort, setSort, style = {} }) {
  const active = sort?.key === sortKey;
  const next = () => setSort(active && sort.dir === 'asc' ? { key: sortKey, dir: 'desc' }
    : active && sort.dir === 'desc' ? null            // third click clears — back to the natural order
    : { key: sortKey, dir: 'asc' });
  return (
    <th style={{ ...ADMIN_TH, ...style, cursor: 'pointer', userSelect: 'none' }}
      onClick={next}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={`Sort by ${typeof label === 'string' ? label.toLowerCase() : (sortKey || 'this column')}`}>
      {label}
      <span style={{ marginLeft: 5, opacity: active ? 1 : 0.25, fontSize: 10 }}>
        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '▲'}
      </span>
    </th>
  );
}
// One line per address. break-all used to split an email mid-word onto a second
// row ("…therapieclinic.co" / "m"), which cost a row of height on every user and
// made the column hard to scan; the full value stays available via title.
const EMAIL_CELL = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 };

// ── Access-log detail parsing (Country + IP columns) ───────────────────────────
// The stored detail is either "CC · <ip>" (geo-stamped events), a bare IP
// (rows from before geo), or freeform text ("Reset by …", "Clinic: …").
const DETAIL_GEO_RE = /^([A-Z]{2}) · (.+)$/;
const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 };

function splitAccessDetail(detail) {
  const m = DETAIL_GEO_RE.exec(detail || '');
  return m ? { country: m[1], rest: m[2] } : { country: null, rest: detail || null };
}

function regionName(code) {
  try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(code); }
  catch { return code; }
}

/** IPs (v4/v6) render monospace, right-aligned to the column edge; anything
 *  else (freeform detail text) stays regular left-aligned type. */
function AccessDetailText({ value }) {
  if (/^[0-9a-f:.]+$/i.test(value || '')) {
    return <span style={{ ...MONO, display: 'block', textAlign: 'right' }}>{value}</span>;
  }
  return value || null;
}

/** One timestamp cell, two anchors: date pinned left, time pinned right — so
 *  both read as clean sub-columns without splitting the table column. No
 *  seconds: minute precision is plenty for an audit list and saves width. */
function DateTimeCell({ value }) {
  if (!value) return '—';
  const d = new Date(value);
  return (
    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
      <span>{d.toLocaleDateString()}</span>
      <span>{d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
    </span>
  );
}

function AdminTableWrap({ children, empty, loading, emptyMessage }) {
  if (loading) return <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '16px 0' }}>Loading…</div>;
  if (empty) return <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '16px 0' }}>{emptyMessage}</div>;
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        {children}
      </table>
    </div>
  );
}

function AdminFilterToolbar({ email, onEmail, emailLabel, placeholder, searchWidth, chips, children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200,
        flex: searchWidth ? `0 1 ${searchWidth}px` : '1 1 200px' }}>
        <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500 }}>{emailLabel || 'Email'}</span>
        <input
          type="search"
          placeholder={placeholder || 'Filter by email…'}
          aria-label={emailLabel || 'Filter by email'}
          value={email}
          onChange={onEmail}
          style={{
            width: '100%', padding: '7px 10px', fontSize: 12,
            border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
          }}
        />
      </label>
      {chips}
      {children}
    </div>
  );
}

// ── Section: Users & access (Entra + active PIN users) ─────────────────────────

// Sign-in filter inside All users. Every role needs a PIN and the sign-in event
// is only logged past the PIN screen, so "no PIN" means never got into the app.
const SIGN_IN_FILTERS = [
  { id: 'all',    label: 'Any' },
  { id: 'in',     label: 'Signed in' },
  { id: 'never',  label: 'Never signed in' },
];

const USER_ACCESS_VIEWS = [
  { id: 'active', label: 'All users' },
  { id: 'direct', label: 'Direct access' },
  { id: 'groups', label: 'Groups' },
];

/** Direct Entra assignment wins; otherwise role from a group app-role grant. */
function resolveUserRoleDisplay(email, roleByEmail, inheritedByEmail) {
  const key = (email || '').toLowerCase();
  const direct = roleByEmail[key];
  if (direct) return { label: direct, inherited: false };
  const inh = inheritedByEmail[key];
  if (inh) return { label: inh.roleLabel || inh.role, inherited: true, groupName: inh.groupName };
  return null;
}

/** Small "group of people" glyph marking a role inherited from an Entra group —
 *  the group name lives in the hover tooltip so the row stays one line. */
function InheritedGroupIcon({ groupName }) {
  const label = `Inherited through ${groupName}`;
  return (
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" role="img" aria-label={label}
      style={{ width: 14, height: 14, color: 'var(--text-3)', flexShrink: 0 }}
    >
      <title>{label}</title>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/** Globe glyph standing in for the "Country" column header — the full word ate
 *  a disproportionate slice of the row; the icon carries a title/aria-label. */
function CountryHeaderIcon() {
  return (
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Country"
      style={{ width: 15, height: 15, verticalAlign: 'middle', color: 'currentColor' }}
    >
      <title>Country</title>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function UserRoleCell({ email, roleByEmail, inheritedByEmail }) {
  const rd = resolveUserRoleDisplay(email, roleByEmail, inheritedByEmail);
  if (!rd) return '—';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text)' }}>
      <span>{rd.label}</span>
      {rd.inherited && <InheritedGroupIcon groupName={rd.groupName} />}
    </div>
  );
}

function UsersSecuritySection() {
  const { user } = useAuth();
  const { config, updateConfig } = useAppConfig();
  const pinLock  = usePinLock();
  const [data, setData]         = useState(null);
  const [pinRows, setPinRows]   = useState([]);
  // { key, dir } — null until a header is clicked, which keeps each view's
  // existing default order (alphabetical by email) as the starting point.
  const [sort, setSort] = useState(null);
  // { 'bath@therapieclinic.com': { active, pending, declined, other } } — lets the
  // Active-users list show what each clinic has outstanding.
  const [clinicStaff, setClinicStaff] = useState(null);
  const [error, setError]       = useState(null);
  const [busyId, setBusyId]     = useState(null);
  const [pinBusy, setPinBusy]   = useState(null);
  const [confirmPin, setConfirmPin] = useState(null);
  const [editStaffEmail, setEditStaffEmail] = useState(null);   // clinic mailbox whose staff we're editing
  const [adding, setAdding]     = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole]   = useState('');
  const [addBusy, setAddBusy]   = useState(false);
  const [addError, setAddError] = useState(null);
  const [filter, setFilter]     = useState('');
  const [view, setView]         = useState('active');
  const [signIn, setSignIn]     = useState('all');
  const [page, setPage]         = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  // Single-line rows since the inherited-group icon (v0.41.9) — 18 rows keeps
  // this card level with the Access log card (also 18) when they share a row.
  const PAGE_SIZE = 18;

  async function load() {
    setRefreshing(true);
    setError(null);
    try {
      const [access, pin] = await Promise.all([
        getAccessUsers(),
        getPinStatus({ all: true }),
      ]);
      // /api/access already returns inheritedByEmail for group members (one bulk
      // group-member enumeration). Only per-user-resolve the active users that map
      // DIDN'T cover and who aren't directly assigned — the old code re-resolved
      // every active user (~2 Graph calls each), which was the bulk of the load.
      const bulkInherited = access?.inheritedByEmail || {};
      const directEmails = new Set(
        (access?.users || [])
          .filter(u => u.principalType !== 'Group' && u.email)
          .map(u => u.email.toLowerCase()),
      );
      const gapEmails = (pin?.users || [])
        .map(r => r.email)
        .filter(Boolean)
        .filter(e => {
          const k = e.toLowerCase();
          return !bulkInherited[k] && !directEmails.has(k);
        });
      let inheritedExtra = {};
      if (gapEmails.length) {
        try {
          inheritedExtra = (await resolveInheritedAccess(gapEmails))?.inheritedByEmail || {};
        } catch { /* bulk map from getAccess remains fallback */ }
      }
      setData({
        ...access,
        inheritedByEmail: { ...bulkInherited, ...inheritedExtra },
      });
      setPinRows(pin?.users || []);
      setClinicStaff(pin?.clinicStaff || null);
    } catch (e) {
      setError(e.message || 'Could not load users.');
      setData({ roles: [], users: [] });
      setPinRows([]);
    } finally {
      setRefreshing(false);
    }
  }
  useEffect(() => { load(); }, []);

  const roles = data?.roles || [];
  const allUsers = data?.users || [];
  const directUsers = allUsers.filter(u => u.principalType !== 'Group');
  const groupRows   = allUsers.filter(u => u.principalType === 'Group');
  const roleByEmail = Object.fromEntries(
    directUsers.filter(u => u.email).map(u => [(u.email || '').toLowerCase(), u.roleLabel || u.role]),
  );
  // A clinic mailbox has no personal PIN; its named staff do. Detect its rows so
  // the PIN table offers "Edit staff" instead of a meaningless "Reset PIN".
  const isMailboxByEmail = Object.fromEntries(
    directUsers.filter(u => u.email).map(u => [(u.email || '').toLowerCase(), u.role === 'ClinicMailbox']),
  );
  const inheritedByEmail = data?.inheritedByEmail || {};

  const q = filter.trim().toLowerCase();

  // A clinic mailbox has no personal PIN, so it never appeared in this list —
  // which hid a clinic exactly when it had an access request waiting. List every
  // clinic mailbox alongside the PIN holders, whether or not anyone has set one.
  const pinEmails = new Set(pinRows.map(r => (r.email || '').toLowerCase()));
  const mailboxRows = directUsers
    .filter(u => u.role === 'ClinicMailbox' && u.email && !pinEmails.has(u.email.toLowerCase()))
    .map(u => ({ email: u.email, displayName: u.displayName, isMailboxOnly: true }));
  // Plus everyone else with access (group members, direct assignments) who has
  // no PIN yet — listed as never signed in, so access is visible before onboarding.
  const allPeople = buildAllUserRows({ pinRows, mailboxRows, inheritedByEmail, directUsers });

  // Names come from Entra — the direct assignment or the group membership — so
  // the list can be searched by name as well as email (PIN rows carry neither).
  const nameByEmail = {};
  for (const [e, inh] of Object.entries(inheritedByEmail)) if (inh?.displayName) nameByEmail[e] = inh.displayName;
  for (const u of directUsers) if (u.email && u.displayName) nameByEmail[u.email.toLowerCase()] = u.displayName;
  const nameOf = (email) => nameByEmail[(email || '').toLowerCase()] || null;

  const searched = allPeople.filter(r => matchesUserQuery({ email: r.email, name: nameOf(r.email) }, q));
  const signInCounts = {
    all:   searched.length,
    in:    searched.filter(r => !r.neverSignedIn).length,
    never: searched.filter(r => r.neverSignedIn).length,
  };
  const activeFiltered = searched.filter(r => signIn === 'all' || (signIn === 'never') === !!r.neverSignedIn);
  const directFiltered = directUsers.filter(u => matchesUserQuery({ email: u.email, name: u.displayName }, q));
  const groupsFiltered = groupRows.filter(u =>
    !q || (u.displayName || '').toLowerCase().includes(q) || (u.roleLabel || '').toLowerCase().includes(q));

  const pinByEmail = Object.fromEntries(pinRows.map(r => [(r.email || '').toLowerCase(), r]));

  let rows = activeFiltered;
  if (view === 'direct') rows = directFiltered;
  if (view === 'groups') rows = groupsFiltered;

  // Column sorting. With 279 rows the default alphabetical order is no use for
  // "who signed in last" or "which clinics have people waiting", so every column
  // that holds a comparable value sorts. Sorting applies BEFORE pagination, so it
  // orders the whole list rather than the page you happen to be on.
  rows = sortRows(rows, sort, { pinByEmail, roleByEmail, isMailboxByEmail, clinicStaff });

  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const slice = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const from = total ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const to   = Math.min(safePage * PAGE_SIZE, total);

  async function changeRole(u, role) {
    if (!role || role === u.role) return;
    setBusyId(u.assignmentId); setError(null);
    try { await changeAccessRole({ assignmentId: u.assignmentId, principalId: u.principalId, role }); await load(); }
    catch (e) { setError(e.message || 'Change failed.'); }
    finally { setBusyId(null); }
  }
  async function remove(u) {
    if (!window.confirm(`Remove access for ${u.email || u.displayName}?\n\nRole: ${u.roleLabel || u.role}. They lose access on next sign-in.`)) return;
    setBusyId(u.assignmentId); setError(null);
    try { await removeAccessUser(u.assignmentId); await load(); }
    catch (e) { setError(e.message || 'Remove failed.'); }
    finally { setBusyId(null); }
  }
  async function resetPin(email) {
    if (!email) return;
    setPinBusy(email); setError(null);
    try {
      await adminResetPin(email);
      if (pinLock?.recheckStatus && email === user?.email) await pinLock.recheckStatus();
      setConfirmPin(null);
      await load();
    } catch (e) { setError(e.message || 'PIN reset failed.'); }
    finally { setPinBusy(null); }
  }
  async function add(e) {
    e?.preventDefault?.();
    const email = newEmail.trim();
    if (!email || !newRole) { setAddError('Email and role are required.'); return; }
    setAddBusy(true); setAddError(null);
    try { await addAccessUser({ email, role: newRole }); setNewEmail(''); setNewRole(''); setAdding(false); await load(); }
    catch (e2) { setAddError(e2.message || 'Could not add user.'); }
    finally { setAddBusy(false); }
  }

  const viewMeta = {
    active: {
      title: 'All users',
      desc: 'Everyone with access, direct or through an Entra group, signed in or not. Use Sign-in to narrow it.',
      empty: q || signIn !== 'all' ? 'No users match.' : 'No users with access yet.',
      unit: 'users',
    },
    direct: {
      title: 'Direct access',
      desc: 'Users assigned individually in Entra. Change role or remove access here; PIN reset is under All users.',
      empty: q ? 'No direct assignments match.' : 'No direct user assignments.',
      unit: 'users',
    },
    groups: {
      title: 'Groups',
      desc: 'Entra groups granted app roles (e.g. AE-Track-ClinicStaff). Members are listed under All users, signed in or not.',
      empty: q ? 'No groups match.' : 'No group assignments.',
      unit: 'groups',
    },
  }[view];

  // Show + Sign-in as two compact dropdowns on the search row — five chips in
  // two stacked rows took more room than the list they filter.
  const FILTER_LABEL = { fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 };
  const FILTER_TRIGGER = { fontSize: 12, padding: '6px 10px', borderColor: 'var(--border)' };
  const viewChips = (
    <>
      <div>
        <div style={FILTER_LABEL}>Show</div>
        <PlainDropdown value={view} ariaLabel="Show"
          onChange={v => { setView(v); setPage(1); }}
          options={USER_ACCESS_VIEWS.map(v => ({ value: v.id, label: v.label }))}
          triggerClassName="input sm" triggerStyle={FILTER_TRIGGER} style={{ width: 210 }} />
      </div>
      {view === 'active' && (
        <div>
          <div style={FILTER_LABEL}>Sign-in</div>
          <PlainDropdown value={signIn} ariaLabel="Sign-in"
            onChange={v => { setSignIn(v); setPage(1); }}
            options={SIGN_IN_FILTERS.map(f => ({ value: f.id, label: data ? `${f.label} · ${signInCounts[f.id]}` : f.label }))}
            triggerClassName="input sm" triggerStyle={FILTER_TRIGGER} style={{ width: 190 }} />
        </div>
      )}
    </>
  );

  return (
    <div>
      <div className="admin-section-head admin-section-head--start" style={{ marginBottom: 12 }}>
        <div>
          <div className="admin-section-title">
            Users &amp; access
            {data && (
              // Two numbers, not five. "onboarded / active / direct / groups"
              // read as four competing totals for the same thing; the count of
              // rows you are looking at plus how many hold direct access is what
              // the question usually is.
              <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, color: 'var(--text-3)' }}>
                {rows.length} users · {directUsers.length} direct
              </span>
            )}
          </div>
          <div className="admin-section-desc">{viewMeta.desc}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {/* In every view — Add user lived only under Direct access, where nobody
              looked for it. */}
          <button className="btn sm" onClick={() => { setAdding(a => !a); setAddError(null); }} style={{ fontSize: 11 }}>
            {adding ? 'Cancel' : '+ Add user'}
          </button>
          <button className="btn ghost sm" onClick={load} disabled={refreshing} style={{ fontSize: 12 }}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {adding && (
        <form onSubmit={add} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)' }}>
          <StaffEmailInput
            value={newEmail} autoFocus directory
            annotate={e => {
              const rd = resolveUserRoleDisplay(e, roleByEmail, inheritedByEmail);
              return rd && `Already has access — ${rd.label}${rd.inherited ? ' (via group)' : ''}`;
            }}
            onChange={v => { setNewEmail(v); setAddError(null); }}
            inputClass="input sm" selectClass="select"
            style={{ minWidth: 300 }}
            inputStyle={{ fontSize: 12, padding: '4px 8px' }}
            selectStyle={{ fontSize: 12 }}
          />
          <PlainDropdown value={newRole} onChange={setNewRole} placeholder="Select role…" ariaLabel="Role"
            options={roles.map(r => ({ value: r.value, label: r.label }))}
            triggerClassName="input sm" triggerStyle={{ fontSize: 12, padding: '4px 8px' }}
            style={{ width: 200 }} />
          <button className="btn sm" type="submit" disabled={addBusy || !newEmail.trim() || !newRole} style={{ fontSize: 11 }}>
            {addBusy ? 'Adding…' : 'Add user'}
          </button>
          {addError && <span style={{ fontSize: 11, color: 'var(--danger)' }}>{addError}</span>}
        </form>
      )}

      {error && <div className="callout danger" style={{ marginBottom: 12, fontSize: 12 }}>{error}</div>}

      <AdminFilterToolbar
        email={filter}
        onEmail={e => { setFilter(e.target.value); setPage(1); }}
        emailLabel="Search"
        placeholder={view === 'groups' ? 'Search groups…' : 'Name or email…'}
        searchWidth={320}
        chips={viewChips}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'var(--text-3)', marginBottom: 8 }}>
        <span>
          {refreshing && !data ? 'Loading…' : `${total} ${total === 1 ? viewMeta.unit.replace(/s$/, '') : viewMeta.unit}`}
          {total > 0 ? ` · showing ${from}–${to}` : ''}
        </span>
      </div>

      {view === 'active' && (
        <AdminTableWrap loading={!data && refreshing} empty={!slice.length && !!data} emptyMessage={viewMeta.empty}>
          <thead>
            <tr style={{ background: 'var(--surface-2, var(--surface))', borderBottom: '1px solid var(--border)' }}>
              <SortTh label="Last unlock" sortKey="lastUnlock" sort={sort} setSort={setSort} style={{ width: '19%' }} />
              <SortTh label="User"        sortKey="email"      sort={sort} setSort={setSort} style={{ width: '32%' }} />
              <SortTh label="Role"        sortKey="role"       sort={sort} setSort={setSort} style={{ width: '25%' }} />
              <SortTh label="Status" sortKey="status" sort={sort} setSort={setSort} style={{ width: '12%' }} />
              <th style={ADMIN_TH}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {slice.map(row => {
              const locked = row.lockedUntil && new Date(row.lockedUntil) > new Date();
              const isConfirming = confirmPin === row.email;
              return (
                <tr key={row.email} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ ...ADMIN_TD, ...MONO, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                    {row.lastUnlockedAt
                      ? <DateTimeCell value={row.lastUnlockedAt} />
                      : <span style={{ fontStyle: 'italic' }}>Never</span>}
                  </td>
                  <td style={{ ...ADMIN_TD, ...EMAIL_CELL }} title={[nameOf(row.email), row.email].filter(Boolean).join(' · ')}>
                    {nameOf(row.email)
                      ? <><span style={{ fontWeight: 500 }}>{nameOf(row.email)}</span>
                          <span style={{ color: 'var(--text-3)', fontSize: 11.5, marginLeft: 6 }}>{row.email}</span></>
                      : <span style={{ fontWeight: 500 }}>{row.email}</span>}
                  </td>
                  <td style={ADMIN_TD}>
                    <UserRoleCell email={row.email} roleByEmail={roleByEmail} inheritedByEmail={inheritedByEmail} />
                  </td>
                  <td style={{ ...ADMIN_TD,
                    color: locked ? 'var(--danger)'
                      : ((clinicStaff || {})[(row.email || '').toLowerCase()]?.pending ? 'var(--brand-ink, var(--text))' : 'var(--text-3)'),
                    fontWeight: (clinicStaff || {})[(row.email || '').toLowerCase()]?.pending ? 600 : 400 }}>
                    {isMailboxByEmail[(row.email || '').toLowerCase()]
                      ? (() => {
                          const c = (clinicStaff || {})[(row.email || '').toLowerCase()];
                          if (!c) return 'No staff yet';
                          const parts = [];
                          if (c.pending)  parts.push(`${c.pending} waiting`);
                          if (c.active)   parts.push(`${c.active} approved`);
                          if (c.declined) parts.push(`${c.declined} declined`);
                          return parts.length ? parts.join(' · ') : 'No staff yet';
                        })()
                      : row.neverSignedIn ? 'Never signed in · no PIN'
                      : (locked ? `Locked until ${new Date(row.lockedUntil).toLocaleTimeString()}` : 'PIN set')}
                    {!isMailboxByEmail[(row.email || '').toLowerCase()] && row.failedAttempts > 0 ? ` · ${row.failedAttempts} failed` : ''}
                  </td>
                  <td style={ADMIN_TD}>
                    {row.neverSignedIn ? null : isMailboxByEmail[(row.email || '').toLowerCase()] ? (
                      // A shared mailbox has no personal PIN — manage its named staff instead.
                      <button className="btn ghost sm" onClick={() => setEditStaffEmail(row.email)} style={{ fontSize: 11 }}>
                        Edit staff
                      </button>
                    ) : isConfirming ? (
                      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn danger filled sm" onClick={() => resetPin(row.email)} disabled={pinBusy === row.email}>
                          {pinBusy === row.email ? '…' : 'Confirm'}
                        </button>
                        <button className="btn ghost sm" onClick={() => setConfirmPin(null)} style={{ fontSize: 11 }}>Cancel</button>
                      </span>
                    ) : (
                      <button className="btn ghost sm" onClick={() => setConfirmPin(row.email)} style={{ fontSize: 11 }}>
                        Reset PIN
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </AdminTableWrap>
      )}

      {editStaffEmail && (
        <EditClinicStaffModal email={editStaffEmail} onClose={() => { setEditStaffEmail(null); load(); }} />
      )}

      {view === 'direct' && (
        <AdminTableWrap loading={!data && refreshing} empty={!slice.length && !!data} emptyMessage={viewMeta.empty}>
          <thead>
            <tr style={{ background: 'var(--surface-2, var(--surface))', borderBottom: '1px solid var(--border)' }}>
              <SortTh label="User" sortKey="email" sort={sort} setSort={setSort} style={{ width: '34%' }} />
              <SortTh label="Name" sortKey="name"  sort={sort} setSort={setSort} style={{ width: '22%' }} />
              <SortTh label="Role" sortKey="role"  sort={sort} setSort={setSort} style={{ width: '22%' }} />
              <SortTh label="PIN"  sortKey="pin"   sort={sort} setSort={setSort} style={{ width: '12%' }} />
              <th style={{ ...ADMIN_TH, width: '10%', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {slice.map(u => {
              const pin = pinByEmail[(u.email || '').toLowerCase()];
              return (
                <tr key={u.assignmentId} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ ...ADMIN_TD, fontWeight: 500, ...EMAIL_CELL }} title={u.email || ''}>{u.email || '—'}</td>
                  <td style={{ ...ADMIN_TD, color: 'var(--text-3)' }}>{u.displayName || '—'}</td>
                  <td style={ADMIN_TD}>
                    <select className="select" value={u.role || ''} disabled={busyId === u.assignmentId}
                      onChange={e => changeRole(u, e.target.value)} style={{ fontSize: 12, width: '100%' }}
                      aria-label={`Role for ${u.email || u.displayName}`}>
                      {!u.role && <option value="">—</option>}
                      {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </td>
                  <td style={{ ...ADMIN_TD, color: 'var(--text-3)' }}>
                    {pin ? 'Set' : 'Not set'}
                  </td>
                  <td style={{ ...ADMIN_TD, textAlign: 'center' }}>
                    <button className="btn ghost sm" onClick={() => remove(u)} disabled={busyId === u.assignmentId}
                      title={`Remove access for ${u.email || u.displayName || 'this user'}`}
                      aria-label={`Remove access for ${u.email || u.displayName || 'this user'}`}
                      style={{ color: 'var(--danger)', padding: '4px 8px', lineHeight: 0 }}>
                      {busyId === u.assignmentId ? '…' : <TrashIcon size={16} weight="regular" aria-hidden="true" />}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </AdminTableWrap>
      )}

      {view === 'groups' && (
        <AdminTableWrap loading={!data && refreshing} empty={!slice.length && !!data} emptyMessage={viewMeta.empty}>
          <thead>
            <tr style={{ background: 'var(--surface-2, var(--surface))', borderBottom: '1px solid var(--border)' }}>
              <th style={{ ...ADMIN_TH, width: '40%' }}>Group</th>
              <th style={{ ...ADMIN_TH, width: '24%' }}>Role</th>
              <th style={ADMIN_TH}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {slice.map(g => (
              <tr key={g.assignmentId} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ ...ADMIN_TD, fontWeight: 500 }}>{g.displayName || g.principalId}</td>
                <td style={ADMIN_TD}>{g.roleLabel || g.role || '—'}</td>
                <td style={{ ...ADMIN_TD, color: 'var(--text-3)' }}>
                  Members are listed under All users, signed in or not
                </td>
              </tr>
            ))}
          </tbody>
        </AdminTableWrap>
      )}

      {total > 0 && (
        <div style={{ marginTop: 10 }}>
          <Pager page={safePage} pageCount={pageCount} total={total} from={from} to={to}
            onPage={setPage} unit={viewMeta.unit} />
        </div>
      )}

      {(view === 'active' || view === 'direct') && data && (
        <DirectoryMatches
          query={filter}
          listedEmails={new Set([...allPeople, ...directUsers].map(r => (r.email || '').toLowerCase()).filter(Boolean))}
          roleByEmail={roleByEmail}
          inheritedByEmail={inheritedByEmail}
          pinByEmail={pinByEmail}
          onGrant={email => { setAdding(true); setNewEmail(email); setAddError(null); }}
        />
      )}
    </div>
  );
}

const ACCESS_EVENT_LABELS = {
  session_start:      'Signed in',
  subsession_start:   'Staff signed in',
  pin_set:            'PIN set',
  pin_changed:        'PIN changed',
  pin_unlock:         'PIN unlock',
  pin_reset_admin:    'PIN reset (admin)',
  pin_forgot_request: 'Forgot PIN request',
  security_alert:     'IT security alert',
};

const ACCESS_LOG_EVENT_GROUPS = {
  // A clinic's shared mailbox reaching the app, and a named staff member
  // signing in behind it, are both sign-ins — the Sign-ins view shows both.
  logins:   ['session_start', 'subsession_start'],
  pin:      ['pin_set', 'pin_changed', 'pin_unlock', 'pin_reset_admin', 'pin_forgot_request'],
  security: ['security_alert'],
};

const ACCESS_LOG_TIMELINES = [
  { id: '7d',  label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All time' },
];

const ACCESS_LOG_EVENT_VIEWS = [
  { id: 'logins',   label: 'Sign-ins' },
  { id: 'pin',      label: 'PIN activity' },
  // Every unexpected-country alert sent to IT — the record that the escalation
  // actually happened, rather than an inference from the sign-in that caused it.
  { id: 'security', label: 'Security alerts' },
  { id: 'all',      label: 'All events' },
];

function accessLogDateRange(preset) {
  if (preset === 'all') return {};
  const now = new Date();
  const to = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1,
  ));
  if (preset === 'today') {
    const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    return { from: from.toISOString(), to: to.toISOString() };
  }
  const days = { '7d': 7, '30d': 30, '90d': 90 }[preset] || 7;
  const from = new Date(to.getTime() - days * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function AccessLogSection() {
  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [error, setError]       = useState(null);
  const [filter, setFilter]     = useState('');
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [timeline, setTimeline] = useState('7d');
  const [eventView, setEventView] = useState('logins');
  // Carried from a clicked sign-in row into the mute form, so the person and
  // country are not retyped from the alert you just read.
  const [prefill, setPrefill] = useState(null);
  // A new order has to start at page 1 — otherwise you land in the middle of it.
  const sortBy = (next) => { setSort(next); setPage(1); };
  const [refreshing, setRefreshing] = useState(false);
  // Sorting is SERVER-side here, unlike Users & access: the log is paginated, so
  // sorting the eighteen rows on screen would reorder the page, not the log.
  // null = the natural order (newest first), which is what a log should open on.
  const [sort, setSort] = useState(null);
  // 18 single-line rows ≈ the height of Users & access's 10 two-line rows, so
  // the two cards level up when they share a row (20 ran slightly taller).
  const PAGE_SIZE = 18;

  async function load() {
    setRefreshing(true);
    try {
      const range = accessLogDateRange(timeline);
      const events = ACCESS_LOG_EVENT_GROUPS[eventView];
      const data = await getAccessLogs({
        email: search.trim() || undefined,
        events: events || undefined,
        from: range.from,
        to: range.to,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        sort: sort?.key,
        dir:  sort?.dir,
      });
      setLogs(data.logs || []);
      setTotal(data.total ?? 0);
      setError(null);
    } catch (e) {
      setError(e.message);
      setLogs([]);
      setTotal(0);
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => setSearch(filter.trim()), 300);
    return () => clearTimeout(t);
  }, [filter]);

  useEffect(() => { load(); }, [page, search, timeline, eventView, sort?.key, sort?.dir]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to   = Math.min(page * PAGE_SIZE, total);
  const eventViewLabel = ACCESS_LOG_EVENT_VIEWS.find(v => v.id === eventView)?.label || 'Events';

  return (
    <div>
      <div className="admin-section-head admin-section-head--start" style={{ marginBottom: 12 }}>
        <div>
          <div className="admin-section-title">Access log</div>
          <div className="admin-section-desc">
            Sign-ins by default — switch to PIN activity or all events when needed.
          </div>
        </div>
        <button className="btn ghost sm" onClick={load} disabled={refreshing} style={{ fontSize: 12, flexShrink: 0 }}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="callout danger" style={{ marginBottom: 12, fontSize: 12 }}>{error}</div>
      )}

      <AdminFilterToolbar
        email={filter}
        onEmail={e => { setFilter(e.target.value); setPage(1); }}
        chips={(
          <>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>Timeline</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ACCESS_LOG_TIMELINES.map(t => (
                  <AdminFilterChip key={t.id} active={timeline === t.id} onClick={() => { setTimeline(t.id); setPage(1); }}>
                    {t.label}
                  </AdminFilterChip>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>Show</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ACCESS_LOG_EVENT_VIEWS.map(v => (
                  <AdminFilterChip key={v.id} active={eventView === v.id} onClick={() => { setEventView(v.id); setPage(1); }}>
                    {v.label}
                  </AdminFilterChip>
                ))}
              </div>
            </div>
          </>
        )}
      />

      {/* The mute control belongs with the alerts it silences — you read the
          alert, then act on it, without changing screens. */}
      {eventView === 'security' && (
        <SecurityAlertMutes
          defaultEmail={prefill?.email || ''}
          defaultCountry={prefill?.country || ''}
        />
      )}

      {eventView === 'logins' && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 11, color: 'var(--text-3)', marginBottom: 10 }}>
          <span><FlagIcon size={13} weight="fill" color="var(--danger)" style={{ verticalAlign: -2, marginRight: 5 }} />Raised an IT ticket</span>
          <span><FlagIcon size={13} weight="regular" color="var(--text-3)" style={{ verticalAlign: -2, marginRight: 5 }} />Muted — verified travel</span>
          <span style={{ fontStyle: 'italic' }}>Click either to open Security alerts.</span>
        </div>
      )}

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 11, color: 'var(--text-3)', marginBottom: 8,
      }}>
        <span>
          {refreshing ? 'Loading…' : `${total} ${eventViewLabel.toLowerCase()}`}
          {total > 0 ? ` · showing ${from}–${to}` : ''}
        </span>
      </div>

      {!refreshing && logs.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '16px 0' }}>
          {search || eventView !== 'all' || timeline !== 'all'
            ? 'No events match these filters.'
            : 'No access events recorded yet.'}
        </div>
      )}

      {logs.length > 0 && (
        <AdminTableWrap empty={false} loading={false} emptyMessage="">
          <thead>
            <tr style={{ background: 'var(--surface-2, var(--surface))', borderBottom: '1px solid var(--border)' }}>
              <SortTh label="When"  sortKey="createdAt" sort={sort} setSort={sortBy} style={{ width: '19%' }} />
              <SortTh label="User"   sortKey="email"     sort={sort} setSort={sortBy} style={{ width: '30%' }} />
              <SortTh label="Role"   sortKey="role"      sort={sort} setSort={sortBy} style={{ width: '12%' }} />
              <SortTh label="Event"  sortKey="eventType" sort={sort} setSort={sortBy} style={{ width: '13%' }} />
              <SortTh label="IP"     sortKey="detail"    sort={sort} setSort={sortBy} style={{ textAlign: 'right' }} />
              {/* Country and IP are one stored string, so the country header
                  sorts on its code alone — see ACCESS_LOG_SORTS. */}
              <SortTh label={<CountryHeaderIcon />} sortKey="country" sort={sort} setSort={sortBy}
                style={{ width: '5%', textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {logs.map(row => {
              const d = splitAccessDetail(row.detail);
              const byActor = row.actorEmail && row.actorEmail !== row.email;
              // Admin PIN resets store "Reset by <actor>" as detail — the same
              // admin the By-line already names, so show them once.
              const rest = byActor && d.rest?.toLowerCase() === `reset by ${row.actorEmail}`.toLowerCase()
                ? null : d.rest;
              // A sign-in from an unexpected country carries its outcome: it
              // raised a ticket, or a mute deliberately silenced it. Clicking
              // takes you to Security alerts with the mute form prefilled.
              const outcome = row.alerted ? 'alerted' : row.muted ? 'muted' : null;
              const goSecurity = () => {
                if (!outcome) return;
                setEventView('security');
                setPage(1);
                setPrefill({ email: row.email, country: row.country || '' });
              };
              return (
                <tr key={row.id}
                  onClick={goSecurity}
                  title={outcome === 'alerted' ? 'This sign-in raised an IT ticket — open Security alerts'
                       : outcome === 'muted' ? 'Alerts for this person are muted — open Security alerts'
                       : undefined}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    cursor: outcome ? 'pointer' : undefined,
                    background: outcome === 'alerted' ? 'var(--danger-weak, #fef2f2)'
                              : outcome === 'muted' ? 'var(--surface-2)' : undefined,
                    boxShadow: outcome === 'alerted' ? 'inset 3px 0 0 var(--danger)'
                             : outcome === 'muted' ? 'inset 3px 0 0 var(--text-3)' : undefined,
                  }}>
                  <td style={{ ...ADMIN_TD, ...MONO, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                    <DateTimeCell value={row.createdAt} />
                  </td>
                  {/* A clinic signs in as one shared mailbox that any FOH member
                      uses, so the mailbox alone never answered "who signed in".
                      Where the staff member is known they lead, with the mailbox
                      beneath; everyone else is already named by their email. */}
                  <td style={{ ...ADMIN_TD, fontWeight: 500, wordBreak: 'break-all' }}>
                    {row.subName ? (
                      <>
                        <div>{row.subName}</div>
                        <div style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-3)' }}>{row.email}</div>
                      </>
                    ) : row.email}
                  </td>
                  <td style={{ ...ADMIN_TD, color: 'var(--text-3)' }}>{row.role || '—'}</td>
                  <td style={ADMIN_TD}>{ACCESS_EVENT_LABELS[row.eventType] || row.eventType}</td>
                  <td style={{ ...ADMIN_TD, color: 'var(--text-3)' }}>
                    {byActor ? `By ${row.actorEmail}` : ''}
                    {byActor && rest ? ' · ' : ''}
                    {rest ? <AccessDetailText value={rest} /> : (byActor ? '' : '—')}
                  </td>
                  <td style={{ ...ADMIN_TD, color: 'var(--text-3)', textAlign: 'right' }}>
                    {/* Flag in FRONT of the code: a trailing badge pushed the
                        country codes out of their column. The title carries the
                        meaning, since the icon alone does not. */}
                    {outcome === 'alerted' && (
                      <FlagIcon size={14} weight="fill" color="var(--danger)" aria-label="Raised an IT ticket"
                        title="This sign-in raised an IT ticket — open Security alerts"
                        style={{ verticalAlign: -2, marginRight: 6 }} />
                    )}
                    {outcome === 'muted' && (
                      <FlagIcon size={14} weight="regular" color="var(--text-3)" aria-label="Alerts muted"
                        title="Alerts for this person are muted — open Security alerts"
                        style={{ verticalAlign: -2, marginRight: 6 }} />
                    )}
                    {d.country
                      ? <span style={MONO} title={regionName(d.country)}>{d.country}</span>
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </AdminTableWrap>
      )}

      {total > 0 && (
        <div style={{ marginTop: 10 }}>
          <Pager page={page} pageCount={pageCount} total={total} from={from} to={to}
            onPage={setPage} unit="events" />
        </div>
      )}
    </div>
  );
}

// ── Section: Cost Categories ───────────────────────────────────────────────────

function CostCategoriesSection({ categories, onSave }) {
  const [items, setItems]     = useState(() => (categories || []).map(c => ({ ...c })));
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft]     = useState(null);

  function startEdit(item) { setEditKey(item.key); setDraft({ ...item }); }
  function cancelEdit()    { setEditKey(null); setDraft(null); }

  function applyEdit() {
    setItems(prev => prev.map(i => i.key === draft.key ? { ...draft } : i));
    setDirty(true);
    cancelEdit();
  }

  function addCategory() {
    const key = `cost-${Date.now()}`;
    const newItem = { key, label: 'New category', cssClass: 'cost-other', order: (items.length + 1) * 10, archived: false };
    setItems(prev => [...prev, newItem]);
    setDirty(true);
    startEdit(newItem);
  }

  function remove(key) {
    setItems(prev => prev.filter(i => i.key !== key));
    setDirty(true);
    if (editKey === key) cancelEdit();
  }

  async function save() {
    setSaving(true);
    try { await onSave('costCategories', items); setDirty(false); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div className="admin-section-head">
        <div>
          <div className="admin-section-title">Cost Categories</div>
          <div className="admin-section-desc">Line-item types in the case cost panel. Archived categories stay on existing costs but drop out of the picker.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={addCategory} style={{ fontSize: 12 }}>+ Add category</button>
          {dirty && (
            <button className="btn primary" onClick={save} disabled={saving} style={{ fontSize: 12 }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
      </div>

      <div className="admin-stack-sm">
        {items.map(item => (
          <div key={item.key}>
            {editKey === item.key && draft ? (
              <div className="admin-edit-card">
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                    <label style={{ fontSize: 11 }}>Label</label>
                    <input className="input" style={{ fontSize: 13 }} value={draft.label}
                      onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} />
                  </div>
                  <div className="field" style={{ width: 120, marginBottom: 0 }}>
                    <label style={{ fontSize: 11 }}>Order</label>
                    <input className="input" type="number" style={{ fontSize: 13 }} value={draft.order}
                      onChange={e => setDraft(d => ({ ...d, order: Number(e.target.value) || 0 }))} />
                  </div>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: 11 }}>Pill colour</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
                    <CostSwatchPicker value={draft.cssClass} onChange={v => setDraft(d => ({ ...d, cssClass: v }))} />
                    <CostCategoryPill cssClass={draft.cssClass} label={draft.label || 'Preview'} />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <input type="checkbox" checked={!!draft.archived}
                    onChange={e => setDraft(d => ({ ...d, archived: e.target.checked }))} />
                  Archived (hidden from the picker)
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn primary sm" onClick={applyEdit} style={{ fontSize: 12 }}>Apply</button>
                  <button className="btn sm" onClick={cancelEdit} style={{ fontSize: 12 }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className={`admin-list-item${item.archived ? ' is-disabled' : ''}`}>
                <CostCategoryPill cssClass={item.cssClass} label={item.label} />
                <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1 }}>
                  order {item.order}{item.archived && ' · archived'}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn ghost sm" onClick={() => startEdit(item)} style={{ fontSize: 11 }}>Edit</button>
                  <button className="btn ghost sm" onClick={() => remove(item.key)} style={{ fontSize: 11, color: 'var(--danger)' }}>Remove</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section: Currencies ────────────────────────────────────────────────────────

function CurrenciesSection({ currencies, onSave }) {
  const [items, setItems]     = useState(() => (currencies || []).map(c => ({ ...c })));
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [editCode, setEditCode] = useState(null);
  const [draft, setDraft]     = useState(null);

  function startEdit(item) { setEditCode(item.code); setDraft({ ...item }); }
  function cancelEdit()    { setEditCode(null); setDraft(null); }

  function applyEdit() {
    setItems(prev => prev.map(i => i.code === editCode ? { ...draft } : i));
    setDirty(true);
    cancelEdit();
  }

  function addCurrency() {
    const item = { code: 'NEW', symbol: '¤', label: 'New currency', isDefault: false, archived: false };
    setItems(prev => [...prev, item]);
    setDirty(true);
    startEdit(item);
  }

  function remove(code) {
    setItems(prev => prev.filter(i => i.code !== code));
    setDirty(true);
    if (editCode === code) cancelEdit();
  }

  // Exactly one default — setting one clears the others.
  function makeDefault(code) {
    setItems(prev => prev.map(i => ({ ...i, isDefault: i.code === code })));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try { await onSave('currencies', items); setDirty(false); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div className="admin-section-head">
        <div>
          <div className="admin-section-title">Currencies</div>
          <div className="admin-section-desc">Currencies available in the cost panel. The default is pre-selected on new cost entries.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={addCurrency} style={{ fontSize: 12 }}>+ Add currency</button>
          {dirty && (
            <button className="btn primary" onClick={save} disabled={saving} style={{ fontSize: 12 }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
      </div>

      <div className="admin-stack-sm">
        {items.map(item => (
          <div key={item.code}>
            {editCode === item.code && draft ? (
              <div className="admin-edit-card">
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="field" style={{ width: 110, marginBottom: 0 }}>
                    <label style={{ fontSize: 11 }}>Code (ISO)</label>
                    <input className="input" style={{ fontSize: 13 }} value={draft.code}
                      onChange={e => setDraft(d => ({ ...d, code: e.target.value.toUpperCase().slice(0, 3) }))} />
                  </div>
                  <div className="field" style={{ width: 90, marginBottom: 0 }}>
                    <label style={{ fontSize: 11 }}>Symbol</label>
                    <input className="input" style={{ fontSize: 13 }} value={draft.symbol}
                      onChange={e => setDraft(d => ({ ...d, symbol: e.target.value.slice(0, 3) }))} />
                  </div>
                  <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                    <label style={{ fontSize: 11 }}>Label</label>
                    <input className="input" style={{ fontSize: 13 }} value={draft.label}
                      onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <input type="checkbox" checked={!!draft.archived}
                    onChange={e => setDraft(d => ({ ...d, archived: e.target.checked }))} />
                  Archived (hidden from the picker)
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn primary sm" onClick={applyEdit} style={{ fontSize: 12 }}>Apply</button>
                  <button className="btn sm" onClick={cancelEdit} style={{ fontSize: 12 }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className={`admin-list-item${item.archived ? ' is-disabled' : ''}`}>
                <span style={{ fontSize: 13, fontWeight: 600, minWidth: 64 }}>{item.symbol} {item.code}</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1 }}>
                  {item.label}{item.archived && ' · archived'}
                </span>
                {item.isDefault ? (
                  <span className="badge status-inv" style={{ fontSize: 11 }}>Default</span>
                ) : (
                  <button className="btn ghost sm" onClick={() => makeDefault(item.code)} style={{ fontSize: 11 }}>Set default</button>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn ghost sm" onClick={() => startEdit(item)} style={{ fontSize: 11 }}>Edit</button>
                  <button className="btn ghost sm" onClick={() => remove(item.code)} style={{ fontSize: 11, color: 'var(--danger)' }}>Remove</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section: Changelog ─────────────────────────────────────────────────────────

function ImpactBadge({ impact }) {
  const meta = IMPACT[impact] || IMPACT.small;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
      background: meta.bg, color: meta.fg, border: `1px solid ${meta.border}`,
    }}>
      {meta.label}
    </span>
  );
}

function ChangelogSection() {
  const entries = sortedChangelog();
  const current = entries[0]?.version;
  return (
    <div>
      <div className="admin-section-head">
        <div>
          <div className="admin-section-title">Changelog</div>
          <div className="admin-section-desc">
            Release history. New entries are added in <code>src/lib/changelog.js</code> with each shipped version.
          </div>
        </div>
        {current && (
          <span className="badge status-inv" style={{ fontSize: 11 }}>Current · v{current}</span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entries.map(e => (
          <div key={e.version} style={{
            border: '1px solid var(--border)', borderRadius: 10,
            background: 'var(--surface)', padding: '12px 14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>v{e.version}</span>
              <ImpactBadge impact={e.impact} />
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{e.date}</span>
              <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500 }}>· {e.title}</span>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {e.changes.map((c, i) => (
                <li key={i} style={{ fontSize: 12, color: 'var(--text-2, var(--text-3))', lineHeight: 1.5 }}>{c}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main AdminPanel ────────────────────────────────────────────────────────────

// ── Section: Tags (cleanup) ──────────────────────────────────────────────────
// SuperAdmin-only view of the whole tag vocabulary with per-tag usage counts.
// Non-system tags can be hard-deleted (removes the tag + all its case links);
// system tags are shown but locked (never deletable). Labels render as TEXT.

const TagLockIcon = () => (
  <svg className="tag-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ── Per-tag auto-tagging rule editor ─────────────────────────────────────────
// Expandable below each tag row: edit the recursive AND/OR rule, pick a colour,
// save (validated + locked server-side) or clear. Saving locks the tag as system.
function TagRuleEditor({ tag, onSaved }) {
  const { config } = useAppConfig();
  const fieldValues = buildRuleFieldValues(config);
  const [rule, setRule]     = useState(tag.rule || emptyRule());
  const [colour, setColour] = useState(tag.colour || '');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  const [okMsg, setOkMsg]   = useState(null);
  const [preview, setPreview] = useState(null);   // { total, matches } | null
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const previewable = ruleIsPreviewable(rule);

  useEffect(() => {
    // Client-gate: don't fire the preview for a half-typed / disabled rule —
    // it would only flicker a 400 / "Preview failed". Clear stale state instead.
    if (!previewable) {
      setPreviewBusy(false);
      setPreviewError(null);
      setPreview(null);
      return;
    }
    let cancelled = false;   // stale-response guard
    const timer = setTimeout(async () => {   // debounce
      setPreviewBusy(true);
      setPreviewError(null);
      try {
        const r = await previewTagRuleMatches(rule);
        if (!cancelled) { setPreview(r); setPreviewError(null); }
      } catch (e) {
        if (!cancelled) { setPreview(null); setPreviewError(e.message || 'Preview failed.'); }
      } finally {
        if (!cancelled) setPreviewBusy(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [rule, previewable]);

  async function save(clear) {
    setBusy(true); setError(null); setOkMsg(null);
    try {
      await saveTagRule(tag.id, { rule: clear ? null : rule, colour: colour || null });
      setOkMsg(clear ? 'Rule cleared.' : 'Rule saved. Tag is now locked (auto-applied).');
      await onSaved?.();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: '10px 14px', borderTop: '1px dashed var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
        Auto-tagging rule — when a case matches, this tag is applied automatically and locked.
      </div>

      <RuleBuilder value={rule} onChange={setRule} fieldValues={fieldValues} />

      <div style={{ fontSize: 11, color: 'var(--text-3)' }} aria-live="polite">
        {previewBusy && 'Checking matches…'}
        {!previewBusy && !previewable && (
          rule?.enabled === false
            ? 'Rule disabled — it will not auto-apply.'
            : 'Finish every condition to preview matching cases.'
        )}
        {!previewBusy && previewable && preview && (
          <>Matches <strong>{preview.matches}{preview.capped ? '+' : ''}</strong> of {preview.total} case{preview.total === 1 ? '' : 's'}
            {preview.capped && ` (preview scans the first ${preview.scanned})`}</>
        )}
        {!previewBusy && previewable && previewError && (
          <span style={{ color: 'var(--danger)' }}>{previewError}</span>
        )}
      </div>

      <TagColourPicker value={colour} onChange={setColour} />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn sm" onClick={() => save(false)} disabled={busy} style={{ fontSize: 11 }}>
          {busy ? 'Saving…' : 'Save rule & lock'}
        </button>
        {tag.rule && (
          <button className="btn ghost sm" onClick={() => save(true)} disabled={busy} style={{ fontSize: 11 }}>
            Clear rule
          </button>
        )}
        {okMsg && <span style={{ fontSize: 11, color: 'var(--success, #0a7)' }}>{okMsg}</span>}
        {error && <span style={{ fontSize: 11, color: 'var(--danger)' }}>{error}</span>}
      </div>
    </div>
  );
}

// ── Tags ─────────────────────────────────────────────────────────────────────

function TagsSection() {
  const [tags, setTags]       = useState(null);   // null = loading
  const [error, setError]     = useState(null);
  const [busyId, setBusyId]   = useState(null);
  const [editId, setEditId]   = useState(null);   // tag whose rule editor is open
  const [backfill, setBackfill] = useState(null); // last backfill result
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [filter, setFilter]   = useState('');
  const [creating, setCreating] = useState(false); // inline "+ New tag" open
  const [newLabel, setNewLabel] = useState('');
  const [newColour, setNewColour] = useState('');   // '' = neutral
  const [newVisible, setNewVisible] = useState(true); // show in the case tag-picker dropdown
  const [newBusy, setNewBusy]   = useState(false);
  const [newError, setNewError] = useState(null);
  // Inline edit (rename / colour / visibility) — independent of the rule editor.
  const [editTagId, setEditTagId] = useState(null);
  const [ef, setEf]             = useState({ label: '', colour: '', visible: true });
  const [efBusy, setEfBusy]     = useState(false);
  const [efError, setEfError]   = useState(null);

  function openTagEdit(tag) {
    setEditTagId(tag.id);
    setEf({ label: tag.label, colour: tag.colour || '', visible: tag.visibleInDropdown !== false });
    setEfError(null);
  }
  async function saveTagEdit(tag) {
    const label = ef.label.trim();
    if (!label) { setEfError('Label cannot be empty.'); return; }
    setEfBusy(true); setEfError(null);
    try {
      await updateTag(tag.id, { label, colour: ef.colour || null, visibleInDropdown: ef.visible });
      setEditTagId(null);
      await load();
    } catch (e) {
      setEfError(e.message || 'Save failed.');
    } finally {
      setEfBusy(false);
    }
  }

  async function load() {
    setError(null);
    try { setTags(await getTagsAdmin()); }
    catch (e) { setError(e.message || 'Could not load tags.'); setTags([]); }
  }

  useEffect(() => { load(); }, []);

  async function runBackfill() {
    if (!window.confirm('Re-apply all tag rules to every case?\n\nThis adds newly-matching auto-tags and removes ones that no longer match.')) return;
    setBackfillBusy(true); setError(null); setBackfill(null);
    try {
      const r = await backfillTagRules();
      setBackfill(r);
      await load();
    } catch (e) {
      setError(e.message || 'Backfill failed.');
    } finally {
      setBackfillBusy(false);
    }
  }

  async function remove(tag) {
    const n = tag.useCount || 0;
    const where = n === 0
      ? 'It is not applied to any cases.'
      : `It will be removed from ${n} case${n === 1 ? '' : 's'}.`;
    if (!window.confirm(`Delete the tag "${tag.label}"?\n\n${where}\n\nThis cannot be undone.`)) return;

    setBusyId(tag.id);
    setError(null);
    try {
      await deleteTag(tag.id);
      setTags(prev => (prev || []).filter(t => t.id !== tag.id));
    } catch (e) {
      setError(e.message || 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function addTag(e) {
    e?.preventDefault?.();
    const label = newLabel.trim();
    if (!label) { setNewError('Enter a tag label.'); return; }
    setNewBusy(true); setNewError(null);
    try {
      await createTag({ label, colour: newColour || null, visibleInDropdown: newVisible });
      setNewLabel(''); setNewColour(''); setNewVisible(true);
      setCreating(false);
      await load();
    } catch (err) {
      setNewError(err.message || 'Could not create tag.');
    } finally {
      setNewBusy(false);
    }
  }

  const q = filter.trim().toLowerCase();
  const filtered = (tags || []).filter(t => !q || (t.label || '').toLowerCase().includes(q));
  const pg = usePagination(filtered, 20);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
        <div>
          <div className="admin-section-title">Tags &amp; auto-tag rules</div>
          <div className="admin-section-desc">
            Manage the tag vocabulary and auto-tagging rules. Deleting a tag removes it from every case. System/rule tags are locked.
          </div>
        </div>
        {/* flexShrink 0 + nowrap: at capped panel widths the long desc would
            otherwise squeeze these into two-line buttons. */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, whiteSpace: 'nowrap' }}>
          <button className="btn sm" onClick={() => { setCreating(c => !c); setNewError(null); }} style={{ fontSize: 11 }}>
            {creating ? 'Cancel' : '+ New tag'}
          </button>
          <button className="btn ghost sm" onClick={runBackfill} disabled={backfillBusy} style={{ fontSize: 11 }}>
            {backfillBusy ? 'Backfilling…' : 'Re-apply rules'}
          </button>
          <button className="btn ghost sm" onClick={load} style={{ fontSize: 11 }}>Refresh</button>
        </div>
      </div>

      {creating && (
        <form onSubmit={addTag} style={{
          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12,
          padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)',
        }}>
          <input className="input sm" type="text" value={newLabel} autoFocus
                 placeholder="New tag label (e.g. Legal)"
                 onChange={e => { setNewLabel(e.target.value); setNewError(null); }}
                 style={{ fontSize: 12, padding: '4px 8px', minWidth: 200 }}
                 aria-label="New tag label" />
          {/* Colour picker — palette + custom, to differentiate tags at a glance */}
          <TagColourPicker value={newColour} onChange={setNewColour} />
          {/* Visible in dropdown — else the tag is search-only in the case picker */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-2)', cursor: 'pointer' }}>
            <input type="checkbox" className="native" checked={newVisible}
                   onChange={e => setNewVisible(e.target.checked)} />
            Visible in dropdown
          </label>
          <button className="btn sm" type="submit" disabled={newBusy || !newLabel.trim()} style={{ fontSize: 11 }}>
            {newBusy ? 'Creating…' : 'Create tag'}
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-3)', flexBasis: '100%' }}>
            Created as a normal tag (no rule). If not visible in the dropdown, it stays findable by typing. Open a tag to add an auto-tagging rule.
          </span>
          {newError && <span style={{ fontSize: 11, color: 'var(--danger)' }}>{newError}</span>}
        </form>
      )}

      {backfill && (
        <div className="callout" style={{ marginBottom: 12, fontSize: 12 }}>
          Backfill complete: {backfill.cases} case{backfill.cases === 1 ? '' : 's'} checked,
          {' '}{backfill.added} tag{backfill.added === 1 ? '' : 's'} added,
          {' '}{backfill.removed} removed across {backfill.changedCases} case{backfill.changedCases === 1 ? '' : 's'}.
        </div>
      )}

      {error && (
        <div className="callout danger" style={{ marginBottom: 12 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div style={{ fontSize: 12 }}>{error}</div>
        </div>
      )}

      {tags === null && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading tags…</div>
      )}

      {tags !== null && tags.length === 0 && !error && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No tags yet. Tags are created when staff tag a case or when an auto-tag rule first matches.</div>
      )}

      {tags !== null && tags.length > 10 && (
        <ListSearch value={filter} onChange={v => { setFilter(v); pg.setPage(1); }}
          placeholder="Search tags by label…" label="Search tags" />
      )}

      {tags !== null && tags.length > 0 && filtered.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No tags match “{filter}”.</div>
      )}

      {filtered.length > 0 && (
        <div className="admin-stack-sm">
          {pg.slice.map(tag => {
            const n = tag.useCount || 0;
            const open = editId === tag.id;
            return (
              <div key={tag.id} style={{
                borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px' }}>
                  <span className={`badge tag-pill${tag.isSystem ? ' tag-system' : ''}${tagColourClass(tag.colour)}`} style={tagPillStyle(tag.colour)}>
                    {tag.isSystem && <TagLockIcon />}
                    {tag.label}
                  </span>
                  {tag.isSystem && (
                    <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-3)' }}>
                      System
                    </span>
                  )}
                  {tag.rule && (
                    <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-3)' }}>
                      · Rule
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                    {n} case{n === 1 ? '' : 's'}
                  </span>
                  {!tag.isSystem && (
                    <button
                      className="btn ghost sm"
                      onClick={() => (editTagId === tag.id ? setEditTagId(null) : openTagEdit(tag))}
                      style={{ fontSize: 11 }}
                      aria-expanded={editTagId === tag.id}
                    >
                      {editTagId === tag.id ? 'Close' : 'Edit'}
                    </button>
                  )}
                  <button
                    className="btn ghost sm"
                    onClick={() => setEditId(open ? null : tag.id)}
                    style={{ fontSize: 11 }}
                    aria-expanded={open}
                  >
                    {open ? 'Close' : (tag.rule ? 'Edit rule' : 'Add rule')}
                  </button>
                  {tag.isSystem ? (
                    <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <TagLockIcon /> Locked
                    </span>
                  ) : (
                    <button
                      className="btn ghost sm"
                      onClick={() => remove(tag)}
                      disabled={busyId === tag.id}
                      style={{ fontSize: 11, color: 'var(--danger)' }}
                      aria-label={`Delete ${tag.label}`}
                    >
                      {busyId === tag.id ? 'Deleting…' : 'Delete'}
                    </button>
                  )}
                </div>
                {editTagId === tag.id && (
                  <div style={{ padding: '10px 14px', borderTop: '1px dashed var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="field">
                      <label style={{ fontSize: 11 }}>Label</label>
                      <input className="input sm" type="text" value={ef.label}
                             onChange={e => { setEf(p => ({ ...p, label: e.target.value })); setEfError(null); }}
                             style={{ fontSize: 12, padding: '4px 8px', maxWidth: 280 }} aria-label="Tag label" />
                    </div>
                    <TagColourPicker value={ef.colour} onChange={c => setEf(p => ({ ...p, colour: c }))} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-2)', cursor: 'pointer' }}>
                      <input type="checkbox" className="native" checked={ef.visible}
                             onChange={e => setEf(p => ({ ...p, visible: e.target.checked }))} />
                      Visible in dropdown
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button className="btn sm" onClick={() => saveTagEdit(tag)} disabled={efBusy || !ef.label.trim()} style={{ fontSize: 11 }}>
                        {efBusy ? 'Saving…' : 'Save changes'}
                      </button>
                      <button className="btn ghost sm" onClick={() => setEditTagId(null)} style={{ fontSize: 11 }}>Cancel</button>
                      {efError && <span style={{ fontSize: 11, color: 'var(--danger)' }}>{efError}</span>}
                    </div>
                  </div>
                )}
                {open && (
                  <TagRuleEditor
                    key={`${tag.id}-${tag.rule ? 'r' : 'n'}`}
                    tag={tag}
                    onSaved={async () => { await load(); }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > 0 && (
        <Pager page={pg.page} pageCount={pg.pageCount} total={pg.total}
          from={pg.from} to={pg.to} onPage={pg.setPage} unit="tags" />
      )}
    </div>
  );
}

// Ordered by how often they are used, not by how they were built: the tab you
// live in first, the ones you configure once last.
const TABS = [
  'Users & Security', 'Notifications', 'Roles', 'Tags & Rules', 'Cost',
  'Incident Model', 'Case Config', 'Developer', 'Changelog',
];

function AdminTabPanel({ tab, children, className = 'admin-tabpanel' }) {
  const slug = tabSlug(tab);
  return (
    <div
      role="tabpanel"
      id={`admin-tabpanel-${slug}`}
      aria-labelledby={`admin-tab-${slug}`}
      className={className}
    >
      {children}
    </div>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user }  = useAuth();
  const { config, loading, updateConfig, updateConfigBatch, saveError } = useAppConfig();

  // Which half of Users & Security is on screen.
  const [securityView, setSecurityView] = useState('users');

  const [activeTab, setActiveTab] = useState(() => {
    // Deep link from another page (e.g. the notification-rule wizard returns
    // via navigate('/admin', { state: { adminTab: 'Notifications' } })).
    const requested = location.state?.adminTab;
    const tabs = user?.adminTabs;
    if (requested && (tabs === 'all' ? TABS.includes(requested) : tabs?.includes?.(requested))) {
      return requested;
    }
    // Users & Security is the day-to-day landing tab; scoped admins without it
    // fall back to their first allowed tab.
    if (tabs === 'all' || tabs?.includes?.('Users & Security')) return 'Users & Security';
    return tabs?.[0] ?? 'Case Config';
  });
  const [dismissedSaveError, setDismissedSaveError] = useState(false);

  if (!user?.adminTabs) return null;
  // Scoped access (e.g. Supervisor): only the allowed subset of tabs renders,
  // in canonical TABS order. The API enforces the same scope per endpoint/key.
  const allowedTabs = user.adminTabs === 'all' ? TABS : TABS.filter(t => user.adminTabs.includes(t));

  const currentSaveError = dismissedSaveError ? null : saveError;
  // Editors (Notifications, Roles, …) copy config into local draft state ONCE,
  // on mount. Mounting them before the config load lands seeds the drafts with
  // the empty code defaults, and the next Save publishes that over the real
  // config — a refresh on the Notifications tab (location.state survives a
  // reload) showed no rules and a group save wiped the existing groups.
  const shownTab = loading ? null : activeTab;

  return (
    <>
      <header className="page-head">
        <div>
          <div className="breadcrumb">
            <a href="#" onClick={e => { e.preventDefault(); navigate('/dashboard'); }}>Overview</a>
            <span>›</span>
            <span>Admin Panel</span>
          </div>
          <h1>Admin Panel</h1>
          <div className="page-sub">{user.isSuperAdmin ? 'SuperAdmin' : 'Supervisor'} · {user.email}</div>
        </div>
      </header>

      <SaveBanner error={currentSaveError} onDismiss={() => setDismissedSaveError(true)} />

      {/* Tab bar */}
      <AdminTabBar tabs={allowedTabs} active={activeTab} onChange={setActiveTab} />
      {loading && <div className="field-hint" style={{ padding: '16px 0' }}>Loading configuration…</div>}

      {/* ── Case Config tab ──
          Event types, the governed severity/status/treatment taxonomies, the
          legal threshold, and the case-list UI preferences. Panels are grouped
          into two .admin-col stacks (balanced by rough height) so short cards
          pack under each other instead of leaving row-height gaps — nesting
          grids inside a flow cell is still off-limits (editors clip). */}
      {shownTab === 'Case Config' && (
        <AdminTabPanel tab="Case Config" className="admin-flow">
          <div className="admin-col">
            <div className="panel">
              <div className="panel-body">
                <EventTypesSection eventTypes={config.eventTypes} onSave={updateConfig} />
              </div>
            </div>
            <div className="panel">
              <div className="panel-body">
                <ConfigRegistry configKey="statusValues" items={config.statusValues} onPublish={updateConfig} />
              </div>
            </div>
            <div className="panel">
              <div className="panel-body">
                <ConfigRegistry configKey="requirementTypes" items={config.requirementTypes} onPublish={updateConfig} />
              </div>
            </div>
            <div className="panel">
              <div className="panel-body compact">
                {/* Page size + sort are one card — both tiny All Cases prefs. */}
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                    <PageSizeSection pageSize={config.defaultPageSize} onSave={updateConfig} />
                  </div>
                  <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                    <DefaultSortSection defaultSort={config.defaultSort} onSave={updateConfig} />
                  </div>
                </div>
              </div>
            </div>
            <div className="panel">
              <div className="panel-body compact">
                <FilterGroupsSection visible={config.visibleFilterGroups} onSave={updateConfig} />
              </div>
            </div>
          </div>

          <div className="admin-col">
            <div className="panel">
              <div className="panel-body">
                <ConfigRegistry configKey="severityCodes" items={config.severityCodes} onPublish={updateConfig} />
              </div>
            </div>
            <div className="panel">
              <div className="panel-body">
                <ConfigRegistry configKey="treatmentTypes" items={config.treatmentTypes} onPublish={updateConfig} />
              </div>
            </div>
            <div className="panel">
              <div className="panel-body">
                <LegalThresholdSection months={config.legalThresholdMonths} onSave={updateConfig} />
              </div>
            </div>
            <div className="panel">
              <div className="panel-body">
                <SlaSection config={config} onSaveBatch={updateConfigBatch} />
              </div>
            </div>
          </div>
        </AdminTabPanel>
      )}

      {/* ── Incident Model tab ── */}
      {shownTab === 'Incident Model' && (
        <AdminTabPanel tab="Incident Model" className="admin-flow">
          <div className="panel">
            <div className="panel-body">
              <IncidentCategoriesEditor
                tree={config.incidentCategoriesByTreatmentAndCode}
                treatmentTypes={config.treatmentTypes}
                severityCodes={config.severityCodes}
                onPublishBatch={updateConfigBatch}
              />
            </div>
          </div>
        </AdminTabPanel>
      )}

      {/* ── Tags & Rules tab ── */}
      {shownTab === 'Tags & Rules' && (
        <AdminTabPanel tab="Tags & Rules" className="admin-flow">
          <div className="panel">
            <div className="panel-body">
              <TagsSection />
            </div>
          </div>
        </AdminTabPanel>
      )}

      {/* ── Notifications tab ── */}
      {shownTab === 'Notifications' && (
        <AdminTabPanel tab="Notifications" className="admin-flow">
          <div className="panel">
            <div className="panel-body">
              <NotificationsAdmin config={config} onPublish={updateConfig} />
            </div>
          </div>
        </AdminTabPanel>
      )}

      {/* ── Cost tab — categories + currencies side by side when they fit ── */}
      {shownTab === 'Cost' && (
        <AdminTabPanel tab="Cost" className="admin-flow">
          <div className="panel">
            <div className="panel-body">
              <CostCategoriesSection categories={config.costCategories} onSave={updateConfig} />
            </div>
          </div>
          <div className="panel">
            <div className="panel-body">
              <CurrenciesSection currencies={config.currencies} onSave={updateConfig} />
            </div>
          </div>
        </AdminTabPanel>
      )}

      {/* ── Roles tab — custom role definitions (SuperAdmin-only: not in the
          Supervisor tab subset, and the governed key rejects non-SuperAdmin
          writes server-side anyway) ── */}
      {shownTab === 'Roles' && (
        <AdminTabPanel tab="Roles" className="admin-wide">
          <div className="panel">
            <div className="panel-body">
              <RolesAdmin
                config={config}
                onPublish={updateConfig}
                onOpenNotifications={() => setActiveTab('Notifications')}
              />
            </div>
          </div>
        </AdminTabPanel>
      )}

      {/* ── Users & Security tab — users, temporary clinic access, access log ── */}
      {shownTab === 'Users & Security' && (
        // Full width with sub-tabs rather than two half-width panels: both
        // tables have long emails, a role and several action columns, and at
        // half the viewport every cell wrapped onto two lines.
        <AdminTabPanel tab="Users & Security" className="admin-wide">
          <div className="panel">
            <div className="panel-body">
              <div className="tabs-lg" role="tablist" style={{ marginBottom: 18 }}>
                {[
                  { id: 'users',  label: 'Users & access' },
                  // Governed SuperAdmin-only config key — tab hidden otherwise.
                  ...(user?.isSuperAdmin ? [{ id: 'grants', label: 'Temporary clinic access' }] : []),
                  { id: 'log',    label: 'Access log' },
                ].map(t => (
                  <div key={t.id} role="tab" aria-selected={securityView === t.id}
                    className={`tab-lg${securityView === t.id ? ' active' : ''}`}
                    onClick={() => setSecurityView(t.id)}>
                    {t.label}
                  </div>
                ))}
              </div>
              {securityView === 'users' && <UsersSecuritySection />}
              {securityView === 'grants' && user?.isSuperAdmin && (
                <ClinicAccessGrants config={config} onPublish={updateConfig} />
              )}
              {securityView === 'log' && <AccessLogSection />}
            </div>
          </div>
        </AdminTabPanel>
      )}

      {/* ── Developer tab — API reference (docs only; SuperAdmin) ── */}
      {shownTab === 'Developer' && (
        <AdminTabPanel tab="Developer" className="admin-flow">
          <div className="panel">
            <div className="panel-body">
              <DeveloperDocs />
            </div>
          </div>
        </AdminTabPanel>
      )}

      {/* ── Changelog tab ── */}
      {shownTab === 'Changelog' && (
        <AdminTabPanel tab="Changelog" className="admin-flow">
          <div className="panel">
            <div className="panel-body">
              <ChangelogSection />
            </div>
          </div>
        </AdminTabPanel>
      )}
    </>
  );
}
